import {
  CalendarOutlined,
  CloseCircleFilled,
  SwapRightOutlined,
} from '@ant-design/icons';
import { Popover } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { DayPicker, type DateRange } from 'react-day-picker';
import { zhCN } from 'react-day-picker/locale';
import 'react-day-picker/style.css';
import { ADMIN_DATE_FORMAT } from './date';

type DateChangeHandler = (value: Dayjs | null, dateString: string) => void;
type DateRangeValue = [Dayjs, Dayjs] | null;
type DateRangeChangeHandler = (value: DateRangeValue, dateStrings: [string, string]) => void;

interface CommonPickerProps {
  allowClear?: boolean;
  className?: string;
  disabled?: boolean;
  disabledDate?: (current: Dayjs) => boolean;
  format?: string;
  id?: string;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  placeholder?: string;
  status?: 'warning' | 'error';
  style?: CSSProperties;
}

export interface AdminDatePickerProps extends CommonPickerProps {
  value?: Dayjs | null;
  defaultValue?: Dayjs | null;
  onChange?: DateChangeHandler;
  suffixIcon?: ReactNode;
}

export interface AdminDateRangePickerProps extends Omit<CommonPickerProps, 'disabled' | 'placeholder'> {
  value?: DateRangeValue;
  defaultValue?: DateRangeValue;
  onChange?: DateRangeChangeHandler;
  disabled?: boolean | [boolean, boolean];
  placeholder?: [string, string];
  separator?: ReactNode;
  suffixIcon?: ReactNode;
  startLabel?: ReactNode;
  endLabel?: ReactNode;
}

function mergeClassName(...values: Array<string | undefined | false>): string {
  return values.filter(Boolean).join(' ');
}

function formatDate(value: Dayjs | null | undefined, format: string): string {
  return value?.isValid() ? value.format(format) : '';
}

function toNativeDate(value: Dayjs | null | undefined): Date | undefined {
  return value?.isValid() ? value.startOf('day').toDate() : undefined;
}

function toNativeRange(value: DateRangeValue | undefined): DateRange | undefined {
  if (!value) return undefined;
  return { from: toNativeDate(value[0]), to: toNativeDate(value[1]) };
}

function usePickerOpen(
  open: boolean | undefined,
  defaultOpen: boolean | undefined,
  onOpenChange: ((open: boolean) => void) | undefined,
) {
  const [innerOpen, setInnerOpen] = useState(defaultOpen ?? false);
  const resolvedOpen = open ?? innerOpen;
  const changeOpen = (nextOpen: boolean) => {
    if (open === undefined) setInnerOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };
  return [resolvedOpen, changeOpen] as const;
}

function CalendarPanel({ children }: { children: ReactNode }) {
  return <div className="wjs-admin-date-popover">{children}</div>;
}

/** 后台统一单日期选择器：使用独立日历面板，表单值保持 Dayjs。 */
export function AdminDatePicker({
  allowClear = true,
  className,
  defaultOpen,
  defaultValue = null,
  disabled = false,
  disabledDate,
  format = ADMIN_DATE_FORMAT,
  id,
  onChange,
  onOpenChange,
  open,
  placeholder = '请选择日期',
  status,
  style,
  suffixIcon = <CalendarOutlined />,
  value,
}: AdminDatePickerProps) {
  const controlled = value !== undefined;
  const [innerValue, setInnerValue] = useState<Dayjs | null>(defaultValue);
  const selectedValue = controlled ? value ?? null : innerValue;
  const [popoverOpen, setPopoverOpen] = usePickerOpen(open, defaultOpen, onOpenChange);
  const selectedDate = toNativeDate(selectedValue);
  const todayDisabled = disabledDate?.(dayjs().startOf('day')) ?? false;

  const emitChange = (nextValue: Dayjs | null) => {
    if (!controlled) setInnerValue(nextValue);
    onChange?.(nextValue, formatDate(nextValue, format));
  };

  const selectDate = (date: Date | undefined) => {
    if (!date) return;
    emitChange(dayjs(date).startOf('day'));
    setPopoverOpen(false);
  };

  const clear = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    emitChange(null);
  };

  return (
    <Popover
      arrow={false}
      placement="bottomLeft"
      trigger="click"
      open={popoverOpen}
      onOpenChange={nextOpen => { if (!disabled) setPopoverOpen(nextOpen); }}
      classNames={{ root: 'wjs-admin-date-popover-overlay' }}
      content={(
        <CalendarPanel>
          <div className="wjs-admin-date-popover__calendar">
            <DayPicker
              animate
              locale={zhCN}
              mode="single"
              showOutsideDays
              defaultMonth={selectedDate}
              selected={selectedDate}
              disabled={date => disabledDate?.(dayjs(date).startOf('day')) ?? false}
              onSelect={selectDate}
            />
          </div>
          <div className="wjs-admin-date-popover__footer">
            <span>{dayjs().format('YYYY年MM月DD日')}</span>
            <button type="button" disabled={todayDisabled} onClick={() => selectDate(new Date())}>选择今天</button>
          </div>
        </CalendarPanel>
      )}
    >
      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-label={selectedValue ? `已选择 ${formatDate(selectedValue, format)}` : placeholder}
        aria-expanded={popoverOpen}
        className={mergeClassName(
          'wjs-admin-date-trigger',
          status && `wjs-admin-date-trigger--${status}`,
          className,
        )}
        style={style}
      >
        <span className={mergeClassName('wjs-admin-date-trigger__value', !selectedValue && 'is-placeholder')}>
          {selectedValue ? formatDate(selectedValue, format) : placeholder}
        </span>
        <span className="wjs-admin-date-trigger__calendar">{suffixIcon}</span>
        {allowClear && selectedValue ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="清除日期"
            className="wjs-admin-date-trigger__clear"
            onClick={clear}
            onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') clear(event); }}
          >
            <CloseCircleFilled />
          </span>
        ) : null}
      </button>
    </Popover>
  );
}

/** 后台统一日期区间选择器：完成起止日期选择后一次性回写 Dayjs 数组。 */
export function AdminDateRangePicker({
  allowClear = true,
  className,
  defaultOpen,
  defaultValue = null,
  disabled = false,
  disabledDate,
  endLabel = '结束日期',
  format = ADMIN_DATE_FORMAT,
  id,
  onChange,
  onOpenChange,
  open,
  placeholder = ['开始日期', '结束日期'],
  separator = <SwapRightOutlined />,
  startLabel = '开始日期',
  status,
  style,
  suffixIcon = <CalendarOutlined />,
  value,
}: AdminDateRangePickerProps) {
  const controlled = value !== undefined;
  const [innerValue, setInnerValue] = useState<DateRangeValue>(defaultValue);
  const selectedValue = controlled ? value ?? null : innerValue;
  const [popoverOpen, setPopoverOpen] = usePickerOpen(open, defaultOpen, onOpenChange);
  const committedRange = useMemo(() => toNativeRange(selectedValue), [selectedValue]);
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(committedRange);
  const fullyDisabled = Array.isArray(disabled) ? disabled.every(Boolean) : disabled;

  useEffect(() => {
    if (popoverOpen) setDraftRange(committedRange);
  }, [committedRange, popoverOpen]);

  const emitChange = (nextValue: DateRangeValue) => {
    if (!controlled) setInnerValue(nextValue);
    onChange?.(nextValue, [
      formatDate(nextValue?.[0], format),
      formatDate(nextValue?.[1], format),
    ]);
  };

  const selectRange = (range: DateRange | undefined) => {
    setDraftRange(range);
    if (!range?.from || !range.to) return;
    const nextValue: [Dayjs, Dayjs] = [
      dayjs(range.from).startOf('day'),
      dayjs(range.to).startOf('day'),
    ];
    emitChange(nextValue);
    setPopoverOpen(false);
  };

  const clear = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDraftRange(undefined);
    emitChange(null);
  };

  const startValue = selectedValue?.[0] ?? null;
  const endValue = selectedValue?.[1] ?? null;
  const hasValue = Boolean(startValue || endValue);

  return (
    <Popover
      arrow={false}
      placement="bottomLeft"
      trigger="click"
      open={popoverOpen}
      onOpenChange={nextOpen => { if (!fullyDisabled) setPopoverOpen(nextOpen); }}
      classNames={{ root: 'wjs-admin-date-popover-overlay wjs-admin-date-popover-overlay--range' }}
      content={(
        <CalendarPanel>
          <div className="wjs-admin-date-popover__calendar">
            <DayPicker
              animate
              locale={zhCN}
              mode="range"
              numberOfMonths={2}
              showOutsideDays
              defaultMonth={draftRange?.from}
              selected={draftRange}
              disabled={date => disabledDate?.(dayjs(date).startOf('day')) ?? false}
              excludeDisabled
              onSelect={selectRange}
            />
          </div>
          <div className="wjs-admin-date-popover__footer">
            <span>{draftRange?.from && !draftRange.to ? '请选择结束日期' : '请选择开始和结束日期'}</span>
            {hasValue && allowClear ? <button type="button" onClick={event => clear(event)}>清除选择</button> : null}
          </div>
        </CalendarPanel>
      )}
    >
      <button
        id={id}
        type="button"
        disabled={fullyDisabled}
        aria-label={`${typeof startLabel === 'string' ? startLabel : '开始日期'}至${typeof endLabel === 'string' ? endLabel : '结束日期'}`}
        aria-expanded={popoverOpen}
        className={mergeClassName(
          'wjs-admin-date-range-trigger',
          status && `wjs-admin-date-range-trigger--${status}`,
          className,
        )}
        style={style}
      >
        <span className="wjs-admin-date-range-trigger__part">
          <strong className={!startValue ? 'is-placeholder' : undefined}>
            {startValue ? formatDate(startValue, format) : placeholder[0]}
          </strong>
        </span>
        <span className="wjs-admin-date-range-trigger__separator">{separator}</span>
        <span className="wjs-admin-date-range-trigger__part">
          <strong className={!endValue ? 'is-placeholder' : undefined}>
            {endValue ? formatDate(endValue, format) : placeholder[1]}
          </strong>
        </span>
        <span className="wjs-admin-date-range-trigger__calendar">{suffixIcon}</span>
        {allowClear && hasValue ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="清除日期范围"
            className="wjs-admin-date-range-trigger__clear"
            onClick={clear}
            onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') clear(event); }}
          >
            <CloseCircleFilled />
          </span>
        ) : null}
      </button>
    </Popover>
  );
}
